<template>
  <div class="page organisation">
    <div class="content back">
      <div class="left" @click="$router.go(-1)">
        <div class="icon-chevron left" />
        <div class="text">{{ $t('organization.goBack') }}</div>
      </div>
    </div>
    <div class="content title">
      {{ $t('organization.editOrganization.title') }}
    </div>
    <div class="content section">
      <transition name="fade">
        <Notification
          v-if="notification.message"
          class="notification"
          :html-message="notification.message"
          :is-success="notification.success"
        />
      </transition>
      <div class="row">
        <div class="col">
          <div v-if="!fileLink" class="img" @click="openInput">
            <pictureIcon class="logo" />
            <div class="text">{{ $t('organization.addLogo') }}</div>
          </div>
          <div v-else class="img selected" :style="{ 'background-image': `url(${fileLink})` }" @click="openInput">
            <div class="cross" @click.stop="deleteImage">
              <cross class="icon" />
            </div>
          </div>
          <div class="explain">
            {{ $t('organization.logoFormat') }}<br />{{ $t('organization.maxSize', { max: 2 }) }}
          </div>
        </div>
        <div class="col input">
          <div class="title">{{ $t('organization.name') }}</div>
          <input
            id="name"
            v-model="name"
            type="text"
            :class="{ invalid: isSend && (name.length >= 30 || !name.length) }"
            name="name"
            class="text-input opacity"
          />
          <div v-if="isSend && name.length >= 30" class="error">
            {{ $t('organization.maxCharacters', { max: 30 }) }}
          </div>
          <div v-if="isSend && !name.length" class="error">
            {{ $t('organization.errorMessage') }}
          </div>
          <div class="title address">
            {{ $t('organization.editOrganization.address') }}
          </div>
          <textarea id="name" v-model="address" name="name" class="text-input opacity" />
        </div>
        <div
          class="col end"
          :class="{
            error: (isSend && name.length >= 30) || (isSend && !name.length),
          }"
        >
          <div class="delete" @click.stop="removeOrga">
            <binIcon class="icon" />
            {{ $t('organization.editOrganization.deleteOrganization') }}
          </div>
        </div>
      </div>
      <div class="row">
        <div v-if="contributors && contributors.length" class="indicator">
          {{ contributors.length }}
          {{
            contributors.length > 1
              ? $t('organization.editOrganization.person.plural')
              : $t('organization.editOrganization.person.singular')
          }}
        </div>
        <div class="invitation-section">
          <div v-if="!inviteMode" class="main-button button-create indicator create" @click="inviteMode = true">
            <div class="plus-icon" />
            {{ $t('organization.editOrganization.inviteCollaborators') }}
          </div>
          <div v-else class="invite">
            <div class="title">
              {{ $t('organization.editOrganization.inviteCollaborators') }}
            </div>
            <div class="invite-row">
              <input
                id="invitationEmail"
                ref="email"
                v-model="invitationEmail"
                type="text"
                name="invitationEmail"
                class="text-input opacity"
              />
              <div class="cancel" @click="inviteMode = false">
                <cross class="icon" />
              </div>
              <div class="validate" @click="inviteUser">
                <check class="icon" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row users">
        <div v-for="contributor in contributors" :key="contributor.id" class="user">
          <div class="line">
            <div class="name" :class="{ icon: contributor.id === user.id }">
              {{
                contributor.firstName && contributor.lastName ? contributor.firstName + ' ' + contributor.lastName : ''
              }}
            </div>
            <div class="member-since">
              {{
                $t(`dashboard.invitationStatus._${contributor.invitationStatus}`, {
                  since: formatDate(contributor.since || organization.createdAt),
                })
              }}
            </div>
          </div>
          <div class="line">
            <div class="email">
              {{ contributor.email ? contributor.email : '' }}
            </div>
            <div v-if="contributors.length > 1" class="revoque" @click.stop="kickUser(contributor)">
              <doorOutIcon v-if="contributor.id === user.id" class="icon door" />
              <crossArrowIcon v-else class="icon" />
              {{
                contributor.id === user.id
                  ? $t('organization.editOrganization.leaveOrganization')
                  : $t('organization.editOrganization.revokeAccess')
              }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="content submit" :class="{ disable: inviteMode }">
      <div class="main-button button-cancel" @click="$router.go(-1)">
        <div class="text">{{ $t('organization.cancelButton') }}</div>
      </div>
      <div class="main-button button-create" :class="{ disabled: name.length <= 1 }" @click="updateOrga">
        <div class="text">
          {{ $t('organization.editOrganization.confirmButton') }}
        </div>
      </div>
    </div>
    <input
      :ref="'inputFileSettings'"
      type="file"
      accept=".jpg, .jpeg, .png, .gif"
      class="input-file"
      hidden
      @change="uploadFile({}, $event)"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatDate } from '@/libraries/formatter';

import Notification from '@/components/Notification';

import check from '@/assets/icons/checked.svg';
import cross from '@/assets/icons/cross.svg';
import pictureIcon from '@/assets/icons/picture.svg';
import binIcon from '@/assets/icons/bin.svg';
import doorOutIcon from '@/assets/icons/doorOut.svg';
import crossArrowIcon from '@/assets/icons/cross_arrow.svg';

export default {
  name: 'EditOrganisation',
  components: {
    check,
    cross,
    pictureIcon,
    crossArrowIcon,
    doorOutIcon,
    binIcon,
    Notification,
  },
  data: function () {
    return {
      name: '',
      file: null,
      fileLink: '',
      isSend: false,
      fileFormData: null,
      address: '',
      formatDate,
      inviteMode: false,
      invitationEmail: '',
    };
  },
  computed: {
    ...mapGetters(['organization', 'user', 'notification']),
    contributors() {
      return this.organization ? this.organization.users : [];
    },
  },
  watch: {
    organization(orga) {
      if (!orga) return;
      this.name = orga.name;
      this.address = orga.address;
      this.fileLink = orga.logoLink;
    },
    invitationEmail() {
      this.validEmail(this.invitationEmail);
    },
  },
  created() {
    this.$store.dispatch('organization.fetch');
    if (this.organization) {
      this.name = this.organization.name;
      this.address = this.organization.address;
      this.fileLink = this.organization.logoLink;
    }
  },
  methods: {
    validEmail: function (email) {
      var re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const result = re.test(email);
      if (result) {
        this.$refs.email.setCustomValidity('');
      }
      return result;
    },
    async inviteUser() {
      if (this.validEmail(this.invitationEmail)) {
        try {
          await this.$store.dispatch('organization.inviteUser', {
            email: this.invitationEmail,
          });
          const htmlMessage = this.$t('organization.editOrganization.notification.hasBeenAdded', {
            email: this.invitationEmail,
            organization: this.organization.name,
          });
          const notification = {
            message: htmlMessage,
            success: true,
          };
          this.$store.dispatch('sendNotification', notification);
          await this.$store.dispatch('organization.fetch');
          this.inviteMode = false;
          this.invitationEmail = '';
        } catch (e) {
          if (e.response.data.code === 400006) {
            if (e.response.data.error === 'User already in this organization ') {
              const htmlMessage = this.$t('organization.editOrganization.notification.alreadyJoin', {
                email: this.invitationEmail,
                organization: this.organization.name,
              });
              const notification = {
                message: htmlMessage,
                success: false,
              };
              this.$store.dispatch('sendNotification', notification);
            } else {
              const htmlMessage = this.$t('organization.editOrganization.notification.alreadyInOrganization', {
                email: this.invitationEmail,
                organization: this.organization.name,
              });
              const notification = {
                message: htmlMessage,
                success: false,
              };
              this.$store.dispatch('sendNotification', notification);
            }
            this.invitationEmail = '';
            this.inviteMode = false;
          }
        }
      } else {
        this.$refs.email.setCustomValidity('Invalid');
      }
    },
    async logout() {
      await this.$store.dispatch('auth.logout');
      this.$router.push({ name: 'home' });
    },
    async updateOrga() {
      this.isSend = true;
      if (this.name.length >= 30 || !this.name) return;
      if (this.file) {
        await this.$store.dispatch('organization.updatePicture', {
          imageFile: this.file,
        });
      } else if (!this.fileLink) {
        await this.$store.dispatch('organization.deletePicture');
      }
      await this.$store.dispatch('organization.update', {
        name: this.name,
        address: this.address,
      });
      await this.$store.dispatch('organization.fetch');
      await this.$store.dispatch('auth.updateUser');
      this.$router.push({ name: 'dashboard' });
    },
    async removeOrga() {
      try {
        const result = await this.$store.dispatch('setPopup', {
          popupType: 'ConfirmPopup',
          popupData: {
            title: `SUPPRIMER ${this.organization.name}`,
            content: `Tous les dossiers de compétences de l’organisation ${
              this.organization.name
            } seront déplacés dans l’espace personnel de leur créateur original.`,
            company: false,
            confirm: 'SUPPRIMER',
          },
        });
        if (result) {
          await this.$store.dispatch('organization.delete');
          await this.$store.dispatch('auth.updateUser');
          this.$router.push({ name: 'dashboard' });
        }
      } catch (e) {
        console.error(e);
      }
    },
    async uploadFile(element, event) {
      if (!event.target.files[0]) {
        return;
      }
      this.file = event.target.files[0];
      this.fileLink = URL.createObjectURL(event.target.files[0]);
      this.$refs['inputFileSettings'].value = null;
    },
    openInput() {
      this.$refs[`inputFileSettings`].click();
    },
    async kickUser(user) {
      try {
        const result = await this.$store.dispatch('setPopup', {
          popupType: 'ConfirmPopup',
          popupData: {
            title: `QUITTER ${this.organization.name}`,
            content: `Vous perdrez l’accès aux dossiers de compétences de l’organisation`,
            company: this.organization.name,
            confirm: 'QUITTER',
          },
        });
        if (result) {
          await this.$store.dispatch('organization.kickUser', user.id);
          if (user.id !== this.user.id) await this.$store.dispatch('organization.fetch');
          else {
            await this.$store.dispatch('auth.updateUser');
            this.$router.push({ name: 'dashboard' });
          }
        }
      } catch (e) {
        console.error(e);
      }
    },
    async deleteImage() {
      this.fileFormData = null;
      this.file = null;
      this.fileLink = null;
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive/organisation.scss';

.organisation {
  padding: 96px 0 0;

  .content {
    width: 60%;
    padding: 16px;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;

    &.back {
      color: colors.$light-blue-color;
      font-size: 11px;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .left {
        color: colors.$light-blue-color;
        font-size: 11px;
        font-weight: bold;
        display: flex;
        align-items: center;
        cursor: pointer;
        padding: 5px 5px 2px 2px;

        &:hover {
          border-radius: 3px;
          background-color: rgba(colors.$light-blue-color, 0.15);
        }

        .text {
          margin-left: 0;
          margin-bottom: 2px;
        }
      }

      .text {
        margin-left: 8px;
      }
    }

    &.title {
      color: colors.$light-blue-color;
      font-size: 36px;
      font-weight: 600;
    }

    &.section {
      position: relative;
      border-radius: 7px;
      background-color: colors.$white-color;
      box-shadow: 0 1px 7px 0 rgba(167, 167, 167, 0.5);
      padding: 38px 48px 20px 48px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;

      .row {
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        width: 100%;

        &.users {
          flex-direction: column;
        }

        .col {
          display: flex;
          align-items: flex-start;
          justify-content: flex-start;
          flex-direction: column;

          .img {
            box-sizing: border-box;
            height: 123px;
            width: 123px;
            border: 1px dashed colors.$secondary-color;
            border-radius: 11px;
            background-color: #fffcf5;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            cursor: pointer;

            &.selected {
              border: 0;
              position: relative;

              &:after {
                position: absolute;
                content: 'Modifier';
                display: flex;
                align-items: flex-end;
                justify-content: center;
                color: colors.$white-color;
                font-size: 12px;
                font-style: italic;
                font-weight: bold;
                text-align: center;
                background-image: linear-gradient(180deg, rgba(81, 81, 81, 0) 0%, #000000d2 100%);
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat;
                bottom: 0;
                right: 0;
                left: 0;
                height: 60%;
                border-radius: 0 0 11px 11px;
                line-height: 24px;
                opacity: 0;
                transition: all 0.3s;
              }

              &:hover {
                &:after {
                  opacity: 1;
                }
              }

              .cross {
                position: absolute;
                top: -11px;
                right: -11px;
                background: colors.$red-color;
                border-radius: 50%;
                transform: scale(0.75);
                cursor: pointer;
                transition: all 0.3s;
                color: colors.$white-color;

                &:hover {
                  transform: scale(1.05);
                  opacity: 0.8;
                }
              }
            }

            .logo {
              height: 28px;
              width: 31px;
            }

            .text {
              margin-top: 8px;
              width: 54px;
              color: colors.$secondary-color;
              font-size: 15px;
              font-weight: bold;
            }
          }

          .explain {
            margin-top: 6px;
            width: 125px;
            color: #b8b8b8;
            font-size: 12px;
            font-style: italic;
          }

          .title {
            color: #939292;
            font-size: 16px;
            margin-bottom: 6px;
          }

          .error {
            margin: 12px 0;
            font-size: 16px;
            color: colors.$red-color;
          }

          .address {
            margin-top: 12px;
          }

          input {
            width: 280px;

            &.invalid {
              border-color: colors.$red-color;
            }
          }

          &:nth-child(2n) {
            margin-left: 38px;
          }

          &.end {
            display: flex;
            justify-content: flex-end;
            align-items: flex-end;
            width: 100%;
            .delete {
              position: relative;
              top: -4px;
              flex-shrink: 0;
              display: flex;
              align-items: center;
              justify-content: flex-end;
              color: colors.$red-color;
              font-size: 16px;
              font-weight: bold;
              text-align: right;
              cursor: pointer;
              transition: all 0.3s;
              height: 24px;
              &:hover {
                border-radius: 3px;
                background-color: rgba(233, 2, 48, 0.15);
                padding: 0 5px;
              }

              .icon {
                height: 16px;
                width: 16px;
                margin-right: 10px;
              }
            }
          }

          .text-input {
            min-height: 14px;
            padding: 4px 6px;
          }
        }

        .indicator {
          align-self: flex-end;
          margin-right: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 30px;
          width: 140px;
          border-radius: 3px;
          background-color: #e4e4e4;
          line-height: 30px;
          color: #606060;
          font-size: 16px;
          margin-top: 28px;

          &.create {
            width: 203px;
            background-color: colors.$light-blue-color;
            color: colors.$white-color;
            font-size: 11px;
            font-weight: bold;

            .plus-icon {
              margin-right: 8px;
            }

            &:hover {
              cursor: pointer;
            }
          }
        }

        .invite {
          display: flex;
          flex-direction: column;
          align-self: flex-end;

          .title {
            color: #939292;
            font-size: 16px;
            margin-bottom: 8px;
          }

          input {
            width: 280px;
          }
          .invite-row {
            display: flex;

            .validate,
            .cancel {
              box-sizing: border-box;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-left: 16px;
              flex-shrink: 0;
              height: 30px;
              width: 30px;
              border-radius: 5px;
              background-color: colors.$light-blue-color;
              color: colors.$white-color;
              cursor: pointer;
              transition: all 0.3s;

              .icon {
                flex-shrink: 0;
                height: 14px;
                width: 14px;
                transition: all 0.3s;
              }

              &.cancel {
                border: 2px solid colors.$light-blue-color;
                border-radius: 5px;
                background-color: colors.$white-color;

                .icon {
                  height: 30px;
                  width: 30px;
                  color: colors.$light-blue-color;
                }
              }

              &:hover {
                opacity: 0.8;

                .icon {
                  transform: scale(1.1);
                }
              }
            }
          }
        }

        .user {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          width: 100%;
          height: 64px;
          border-top: 1px solid colors.$grey-background-color;
          margin-top: 20px;
          padding-top: 16px;

          &:last-child {
            margin-bottom: 12px;
          }

          .line {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;

            .name {
              color: #000000;
              font-size: 16px;
              display: flex;
              align-items: center;
              text-transform: capitalize;

              &.icon:before {
                position: relative;
                content: '●';
                width: 20px;
                color: colors.$secondary-color;
                margin-right: -6px;
              }
            }

            .email {
              height: 19px;
              width: 165px;
              color: #939292;
              font-family: Lato, sans-serif;
              font-size: 16px;
              line-height: 19px;
            }

            .member-since {
              color: #939292;
              font-size: 16px;
            }

            .revoque {
              color: colors.$light-blue-color;
              font-size: 16px;
              font-weight: bold;
              height: 24px;
              cursor: pointer;
              transition: all 0.3s;
              display: flex;
              align-items: center;

              .icon {
                width: 30px;
                height: 30px;
                margin-right: 5px;

                &.door {
                  margin-right: 3px;
                }
              }

              &:hover {
                border-radius: 3px;
                background-color: rgba(colors.$light-blue-color, 0.15);
                padding-right: 5px;
              }
            }
          }
        }
      }
    }

    &.submit {
      justify-content: flex-end;
      margin-bottom: 32px;

      .main-button {
        margin: 28px 0 0;
      }

      .button-create {
        margin-left: 16px;
      }
    }
  }
}
.notification {
  position: fixed;
  right: 20%;
  top: 102px;
}
</style>
